'use client';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import {
    Flödesspår,
    GRUND,
    Gradientfält,
    Pulsnod,
    ScenVäxlare,
    TYPSNITT,
    hexTillRgba,
    ljus,
    type ScenKontext
} from 'src/components/rörelse';
import { PresentationsNav } from '../komponenter/PresentationsNav';
import { ScenSektion } from '../komponenter/ScenSektion';
import { hittaPresentation } from '../presentationer';
import { BOKSLUTSSTEG, RISKSIGNALER, VYER, ÅRSHJUL } from './autopilot-innehåll';

const KOMBO = 'blå' as const;

const GLÖDPOSITIONER: [number, number][] = [
    [26, 26],
    [72, 34],
    [34, 62],
    [68, 56],
    [44, 40],
    [58, 72]
];

export function AutopilotView() {
    const presentation = hittaPresentation('autopilot');
    const [glödIndex, sättGlödIndex] = useState(0);
    const glöd = useCallback(
        (index: number) => (synlig: boolean) => {
            if (synlig) sättGlödIndex(index);
        },
        []
    );

    return (
        <>
            <Gradientfält
                kombinationer={GLÖDPOSITIONER.map(() => KOMBO)}
                positioner={GLÖDPOSITIONER}
                index={glödIndex}
                styrka={0.4}
            />

            <ScenSektion
                id="inledning"
                kombo={KOMBO}
                nivå="h1"
                rubrik={presentation.titel}
                brödtext={[
                    presentation.ingress,
                    'Bokföringen, bokslutet och årsredovisningen är inte tre system som ska fås att prata med varandra. De är samma material sett i tre skeden.'
                ]}
                onSynlig={glöd(0)}
            >
                <Årshjul />
            </ScenSektion>

            <ScenSektion
                id="aret"
                kombo={KOMBO}
                spegla
                rubrik="Året går runt utan att någon påminner det"
                brödtext={[
                    'Arbetsgivardeklarationen ska in den 12:e. Momsen beror på vilken redovisningsperiod föreningen har. Deklarationsdatumen är inte hämtade någonstans ifrån — de är räknade, ur räkenskapsåret och organisationsformen, med lagrummet noterat vid varje regel.',
                    'Du får veta vad som ska in och när, innan det är för sent att göra något åt det.'
                ]}
                onSynlig={glöd(1)}
            >
                <Box sx={{ width: '100%', maxWidth: 460 }}>
                    <Box sx={{ fontSize: 14, color: GRUND.dämpad, mb: 2 }}>Ur skatteförfarandelagen</Box>
                    {[
                        ['Arbetsgivardeklaration', 'Den 12:e · SFL 26:26 §'],
                        ['Momsdeklaration', 'Beror på period · SFL 26:26 §'],
                        ['Preliminärskatt', 'Den 12:e · SFL 62:3 §'],
                        ['Inkomstdeklaration', 'Efter räkenskapsår'],
                        ['Årsredovisning', 'Sju månader efter bokslut']
                    ].map(([namn, regel]) => (
                        <Box
                            key={namn}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                py: 1.6,
                                borderBottom: `1px solid ${GRUND.kant}`,
                                fontSize: 14.5
                            }}
                        >
                            <Box sx={{ color: GRUND.text }}>{namn}</Box>
                            <Box sx={{ color: GRUND.dämpad, textAlign: 'right' }}>{regel}</Box>
                        </Box>
                    ))}
                </Box>
            </ScenSektion>

            <ScenSektion
                id="bokslut"
                kombo={KOMBO}
                rubrik="Bokslutet är en sekvens, inte ett projekt"
                brödtext={[
                    'Varje steg vet vad det behöver från steget före. Beräkningarna görs åt dig; godkännandet är ditt. Det som saknas står kvar och syns, i stället för att tyst hoppas över.'
                ]}
                onSynlig={glöd(2)}
            >
                {({ framsteg }: ScenKontext) => (
                    <Flödesspår
                        noder={BOKSLUTSSTEG}
                        kombo={KOMBO}
                        riktning="lodrät"
                        framsteg={framsteg}
                        etikett="Bokslutets steg"
                    />
                )}
            </ScenSektion>

            <ScenSektion
                id="signera"
                kombo={KOMBO}
                spegla
                rubrik="Och sedan skriver du under"
                brödtext={[
                    'Handlingarna är framme. Siffrorna är avstämda. Det som återstår är det som bara en människa får göra: att gå i god för dem, med BankID.'
                ]}
                onSynlig={glöd(3)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Pulsnod kombo={KOMBO} etikett="Väntar på din signatur" storlek={18} />
                    <Box
                        sx={{
                            fontFamily: TYPSNITT.display,
                            fontVariationSettings: "'opsz' 72",
                            fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                            fontWeight: 500,
                            letterSpacing: '-0.028em',
                            lineHeight: 1.2,
                            color: ljus(KOMBO),
                            maxWidth: '20ch'
                        }}
                    >
                        Ett moment. Resten är redan gjort.
                    </Box>
                </Box>
            </ScenSektion>

            <ScenSektion
                id="transparens"
                kombo={KOMBO}
                rubrik="Samma siffror, oavsett vem som frågar"
                brödtext={[
                    'Medlemmen behöver inte be styrelsen om årsredovisningen, och styrelsen behöver inte sammanställa något extra för att svara. Det är en och samma bokföring, visad med olika behörighet.'
                ]}
                onSynlig={glöd(4)}
            >
                <Box sx={{ width: '100%', maxWidth: 460 }}>
                    <ScenVäxlare
                        etikett="Styrelsens och medlemmens vy"
                        kombo={KOMBO}
                        höjd={230}
                        scener={VYER.map((v) => ({
                            nyckel: v.nyckel,
                            etikett: v.etikett,
                            innehåll: <Uppgiftskort rubrik={v.rubrik} rader={v.rader} />
                        }))}
                    />
                </Box>
            </ScenSektion>

            <ScenSektion
                id="risk"
                kombo={KOMBO}
                spegla
                vision
                rubrik="Och en kontroll av vilka ni släpper in"
                brödtext={[
                    'Personnummer och organisationsnummer stäms av mot tredjepartsdata om näringsförbud, konkurser och betalningsanmärkningar — innan någon får en roll i föreningen.',
                    'Styrelsen ser en träff, inte ett underlag. Vad kontrollen bygger på och vem som får göras föremål för den är reglerat, och appen håller sig innanför det.'
                ]}
                onSynlig={glöd(5)}
            >
                <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {RISKSIGNALER.map((signal) => (
                        <Box
                            key={signal.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                px: 2,
                                py: 1.5,
                                borderRadius: 1.5,
                                border: `1px ${signal.flaggad ? 'solid' : 'dashed'} ${hexTillRgba(
                                    ljus(KOMBO),
                                    signal.flaggad ? 0.5 : 0.25
                                )}`,
                                bgcolor: hexTillRgba('#12151F', 0.7),
                                fontSize: 14
                            }}
                        >
                            <Box sx={{ color: signal.flaggad ? GRUND.text : GRUND.dämpad }}>{signal.text}</Box>
                            <Box
                                sx={{
                                    fontSize: 12,
                                    whiteSpace: 'nowrap',
                                    color: signal.flaggad ? ljus(KOMBO) : GRUND.dämpad
                                }}
                            >
                                {signal.flaggad ? 'Träff' : 'Ingen träff'}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </ScenSektion>

            <PresentationsNav nuvarande="autopilot" />
        </>
    );
}

function Årshjul() {
    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Flödesspår
                noder={ÅRSHJUL}
                kombo={KOMBO}
                riktning="ring"
                radie={150}
                etikett="Föreningens år, månad för månad"
            />
        </Box>
    );
}

function Uppgiftskort({ rubrik, rader }: { rubrik: string; rader: string[][] }) {
    return (
        <Box
            sx={{
                height: '100%',
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.32)}`,
                bgcolor: hexTillRgba('#12151F', 0.8)
            }}
        >
            <Box
                sx={{
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 36",
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: 2,
                    color: GRUND.text
                }}
            >
                {rubrik}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2, fontSize: 13.5 }}>
                {rader.map(([etikett, värde]) => (
                    <Box key={etikett} sx={{ display: 'contents' }}>
                        <Box sx={{ color: GRUND.dämpad }}>{etikett}</Box>
                        <Box data-tal sx={{ color: GRUND.text, textAlign: 'right' }}>
                            {värde}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
